/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewRef
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { EDriverWorkingState } from 'tacho';

import { IFlatTreeNode, IHierarchyTreeData } from '../../../core/components';
import {
  EDriverStatusSortType,
  EVehicleDriverPanelTabs,
  EVehicleSelectionMode,
  EVehiclesSortType,
  EVehicleStatusType,
  ICoordinates,
  IDriverStatus,
  ISignalStatusUpdate
} from '../../../core/models';
import { ECheckboxState } from '../../../core/models/checkbox-state.enum';
import { StatusSortOptionsComponent } from '../../../shared';
import { VehicleSelectionPanelComponent } from '../vehicle-selection-panel/vehicle-selection-panel.component';

@Component({
  selector: 'fm-vehicle-driver-panel-small',
  templateUrl: './vehicle-driver-panel-small.component.html',
  styleUrls: ['./vehicle-driver-panel-small.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehicleDriverPanelSmallComponent implements AfterViewInit, OnChanges {
  @Input()
  public set activeTab(tab: EVehicleDriverPanelTabs) {
    this._activeTab = tab;
    this._isViewReady$
      .pipe(
        filter(isReady => isReady),
        take(1)
      )
      .subscribe(() => this._activateTab(tab));
  }
  public get activeTab(): EVehicleDriverPanelTabs {
    return this._activeTab;
  }

  @Input()
  public maxPlottableVehicles: number;
  @Input()
  public vehicleListScopingTreeData: IFlatTreeNode<string>[];
  @Input()
  public vehicleListTreeData: IHierarchyTreeData<string>;
  @Input()
  public vehicleSelectionMode: EVehicleSelectionMode;
  @Input()
  public vehicleSortTypeSelected: EVehiclesSortType;
  @Input()
  public isVehicleScopingEnabled: boolean;
  @Input()
  public isAssetsEnabled: boolean;
  @Input()
  public showTachoInformation: boolean;
  @Input()
  public driverStatusData: IDriverStatus[];
  @Input()
  public driverStatusFilter: EDriverWorkingState;
  @Input()
  public isDriverBreakDueFilterSelected: boolean;
  @Input()
  public driverStatusSortType: EDriverStatusSortType;
  @Input()
  public isDriverPlannerPageAllowed: boolean;
  @Input()
  public anyPoweredAssets: boolean;
  @Input()
  public anyNonPoweredAssets: boolean;
  @Input()
  public statusFilterSelected: EVehicleStatusType;
  @Input()
  public statusFilterCounts: Map<EVehicleStatusType, number>;
  @Input()
  isStatusPlotsLoaded: boolean;


  @Output()
  public activeTabChange: EventEmitter<EVehicleDriverPanelTabs> = new EventEmitter();
  @Output()
  public sortParamChange: EventEmitter<EVehiclesSortType> = new EventEmitter();
  @Output()
  public vehicleListEditScopingClick = new EventEmitter<TemplateRef<any>>();
  @Output()
  public vehicleListScopingClose = new EventEmitter<void>();
  @Output()
  public vehicleListScopingSave = new EventEmitter<string[]>();
  @Output()
  public vehicleListSelectFocusVehicle = new EventEmitter<string>();
  @Output()
  public vehicleListSelectionModeChange = new EventEmitter<EVehicleSelectionMode>();
  @Output()
  public vehicleListSelectionChange = new EventEmitter<Map<string, ECheckboxState>>();
  @Output()
  public zoomTo: EventEmitter<ICoordinates> = new EventEmitter();
  @Output()
  public signalChanges: EventEmitter<ISignalStatusUpdate[]> = new EventEmitter();
  @Output()
  public filterChange: EventEmitter<EDriverWorkingState> = new EventEmitter();
  @Output()
  public driverBreakDueFilterChanged: EventEmitter<boolean> = new EventEmitter();
  @Output()
  public driverStatusSortTypeChanged: EventEmitter<EDriverStatusSortType> = new EventEmitter();
  @Output()
  public openDriverPlanner = new EventEmitter<void>();
  @Output()
  public statusFilterChanged = new EventEmitter<EVehicleStatusType>();

  public EVehicleDriverPanelTabs = EVehicleDriverPanelTabs;
  public EVehiclesSortType = EVehiclesSortType;
  public hasFleetSelected: boolean;
  public hasFleetConnected: boolean;
  public isStatusPanelReady = false;

  @ViewChild('panel', { read: ElementRef })
  private readonly _panelElement: ElementRef;
  @ViewChild('tabContent', { read: ViewContainerRef })
  private readonly _tabContent: ViewContainerRef;
  @ViewChild('vehicleListTpl')
  private readonly _vehicleListTpl: TemplateRef<any>;
  @ViewChild(VehicleSelectionPanelComponent)
  private readonly _vehicleSelectionPanel: VehicleSelectionPanelComponent;
  @ViewChild('vehicleStatusTpl')
  private readonly _vehicleStatusTpl: TemplateRef<any>;
  @ViewChild('driverStatusTpl')
  private readonly _driverStatusTpl: TemplateRef<any>;
  @ViewChild(StatusSortOptionsComponent)
  statusSortOptions: StatusSortOptionsComponent;

  private readonly _backdropVisibleClass = 'vehicle-driver-panel-small__backdrop--visible';
  private readonly _panelHeaderHeight = 80;

  private _activeTab: EVehicleDriverPanelTabs;
  private _initialPanPosition: number;
  private _initialPanHeight: number;
  private _panelHeight: number;
  private _panelMovementDirection: number;
  private _vehicleListView: ViewRef;
  private _vehicleStatusView: ViewRef;
  private _driverStatusView: ViewRef;
  private readonly _isViewReadySubject = new BehaviorSubject<boolean>(false);
  private readonly _isViewReady$: Observable<boolean>;

  constructor() {
    this._isViewReady$ = this._isViewReadySubject.asObservable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.statusFilterCounts?.currentValue) {
      this._hasFleetSelected();
    }

    if (changes?.vehicleListTreeData?.currentValue) {
      this._hasFleetConnected();
    }
    this.isStatusPanelReady = true;
  }

  public ngAfterViewInit(): void {
    this._isViewReadySubject.next(true);
    this._setPanelHeight(this._panelHeaderHeight);
  }

  public onClick(): void {
    this._panelHeight = this._panelElement.nativeElement.parentElement.offsetHeight;
    const downPosition = this._panelHeight - this._panelHeaderHeight;
    if (this._panelElement.nativeElement.offsetTop === downPosition) {
      this._executeWithTransition(() => {
        this._setPanelTopAndHeight(0, this._panelHeight);
        this._showBackdrop();
        this._updateVehicleSelectionPanel();
      });
    }
  }

  public panDownPanel(): void {
    this._setPanelTopAndHeight(this._panelHeight - this._panelHeaderHeight, this._panelHeight);
  }

  public onPan(event: HammerInput): void {
    this._panelMovementDirection = event.direction;
    const currentTop = this._panelElement.nativeElement.offsetTop;
    const newTop = this._initialPanPosition + event.deltaY;
    const topTop = 0;
    const bottomTop = this._panelHeight - this._panelHeaderHeight;

    if (newTop <= topTop) {
      if (currentTop !== topTop) {
        this._setPanelTopAndHeight(topTop, this._panelHeight);
      }
    } else if (newTop >= bottomTop) {
      if (currentTop !== bottomTop) {
        this._setPanelTopAndHeight(bottomTop, this._panelHeaderHeight);
      }
    } else {
      this._setPanelTopAndHeight(newTop, this._initialPanHeight - event.deltaY);
    }
  }

  public onPanEnd(): void {
    this._executeWithTransition(() => {
      if (this._panelMovementDirection === Hammer.DIRECTION_UP) {
        this._setPanelTopAndHeight(0, this._panelHeight);
        this._updateVehicleSelectionPanel();
      } else {
        this._setPanelTopAndHeight(`calc(100% - ${this._panelHeaderHeight}px)`, `${this._panelHeaderHeight}px`, false);
        this._hideBackdrop();
      }
    });
  }

  public onPanStart(): void {
    this._initialPanPosition = this._panelElement.nativeElement.offsetTop;
    this._initialPanHeight = this._panelElement.nativeElement.offsetHeight;
    this._panelHeight = this._panelElement.nativeElement.parentElement.offsetHeight;
    this._showBackdrop();
  }

  public onVehicleListEditScopingClick(modalTemplate: TemplateRef<any>): void {
    this.vehicleListEditScopingClick.emit(modalTemplate);
  }

  public onVehicleListScopingClose(): void {
    this.vehicleListScopingClose.emit();
  }

  public onVehicleListScopingSave(selectedGroups: string[]): void {
    this.vehicleListScopingSave.emit(selectedGroups);
  }

  public onVehicleListSelectFocusVehicle(vehicleId: string) {
    this.vehicleListSelectFocusVehicle.emit(vehicleId);
  }

  public onSortParamChange(type: EVehiclesSortType): void {
    this.sortParamChange.emit(type);
  }

  public onVehicleListSelectionChange(vehicleSelection: Map<string, ECheckboxState>): void {
    this.vehicleListSelectionChange.emit(vehicleSelection);
  }

  public onVehicleListSelectionModeChange(mode: EVehicleSelectionMode): void {
    this.vehicleListSelectionModeChange.emit(mode);
  }

  public onDriverBreakDueFilterSelectedChange(selectedBreakDue: boolean): void {
    this.driverBreakDueFilterChanged.emit(selectedBreakDue);
  }

  public selectTab(tab: EVehicleDriverPanelTabs): void {
    this.activeTabChange.next(tab);
  }

  public zoomToPosition(position: ICoordinates): void {
    this.zoomTo.emit(position);
  }

  public onStatusSortTypeChange(statusSortType: EDriverStatusSortType): void {
    this.driverStatusSortTypeChanged.emit(statusSortType);
  }

  public onFilterChange(driverState: EDriverWorkingState): void {
    this.filterChange.emit(driverState);
  }

  public onOpenDriverPlanner(): void {
    this.openDriverPlanner.emit();
  }

  public onStatusFilterChange(filterSelected: EVehicleStatusType): void {
    this.statusFilterChanged.emit(filterSelected);
  }

  public openVehicleDriverSortPanel(): void {
    this.statusSortOptions.openDropdownPanel();
  }

  private _hasFleetConnected(): void {
    this.hasFleetConnected = this.vehicleListTreeData.data && this.vehicleListTreeData.data.length > 1;
  }

  private _hasFleetSelected(): void {
    this.hasFleetSelected = this.statusFilterCounts?.get(EVehicleStatusType.All) > 0;
  }

  private _executeWithTransition(wrappedMethod: () => void): void {
    this._panelElement.nativeElement.style.transition = `all 192ms ease-out`;
    wrappedMethod();
    setTimeout(
      () => {
        this._panelElement.nativeElement.style.transition = `background-color 192ms ease-out`;
      },
      parseInt('192ms', 10)
    );
  }

  private _hideBackdrop(): void {
    this._panelElement.nativeElement.classList.remove(this._backdropVisibleClass);
  }

  private _setPanelHeight(height: string | number, appendPx = true): void {
    this._panelElement.nativeElement.style.height = appendPx ? `${height}px` : `${height}`;
  }

  private _setPanelTopAndHeight(top: string | number, height: string | number, appendPx = true): void {
    this._panelElement.nativeElement.style.top = appendPx ? `${top}px` : `${top}`;
    this._setPanelHeight(height, appendPx);
  }

  private _showBackdrop(): void {
    this._panelElement.nativeElement.classList.add(this._backdropVisibleClass);
  }

  private _activateTab(tab: EVehicleDriverPanelTabs): void {
    this._setPanelHeight(this._panelElement.nativeElement.parentElement.offsetHeight);
    this._tabContent.detach();
    switch (tab) {
      case EVehicleDriverPanelTabs.VehicleList:
        if (this._vehicleListView) {
          this._vehicleListView.destroy();
        }
        this._vehicleListView = this._vehicleListTpl.createEmbeddedView(null);
        this._tabContent.insert(this._vehicleListView);
        break;
      case EVehicleDriverPanelTabs.DriverStatus:
        if (!this._driverStatusView) {
          this._driverStatusView = this._driverStatusTpl.createEmbeddedView(null);
        }
        this._tabContent.insert(this._driverStatusView);
        break;

      default:
        if (!this._vehicleStatusView) {
          this._vehicleStatusView = this._vehicleStatusTpl.createEmbeddedView(null);
        }
        this._tabContent.insert(this._vehicleStatusView);
        break;
    }
  }

  private _updateVehicleSelectionPanel(): void {
    if (this._panelElement.nativeElement.offsetHeight !== this._panelHeight) {
      requestAnimationFrame(() => {
        this._updateVehicleSelectionPanel();
      });
    } else if (this._vehicleSelectionPanel) {
      this._vehicleSelectionPanel.onViewportChange();
    }
  }
}
